import Vue from 'vue'
import VueRouter from 'vue-router'
import {CABINET_LAYOUT, EMPTY_LAYOUT} from "@/layouts/layoutConst";
import {CABINET_MAIN_PAGE, ROUTES, DEFAULT_TITLE, goToClientCabinet} from "@/router/routerConst";

import {useUserStore} from "@/modules/User/store/user";
import {useLayoutStore} from "@/store/layout";

Vue.use(VueRouter)

const AUTH_LOADING_KEY = 'auth-loading'

const routes = [
  {
    path: '/',
    redirect: {name: CABINET_MAIN_PAGE}
  },
  {
    path: '/login',
    name: ROUTES.LOGIN,
    component: () => import(/* webpackChunkName: "MainPages" */ '@/modules/Auth/pages/FlLogin.vue'),
    meta: {
      title: 'Вход',
      layout: EMPTY_LAYOUT,
      requiresAuth: false
    }
  },
  {
    path: '/registration',
    name: ROUTES.REGISTRATION,
    component: () => import('@/modules/Auth/pages/FlRegistration.vue'),
    meta: {
      title: 'Регистрация',
      layout: EMPTY_LAYOUT,
      requiresAuth: false,
    }
  },
  {
    path: '/verification',
    name: ROUTES.VERIFICATION,
    component: () => import('@/modules/Auth/pages/FlVerification.vue'),
    meta: {
      title: 'Подтверждение телефона',
      layout: EMPTY_LAYOUT,
      requiresAuth: false,
    }
  },
  {
    path: '/inspections',
    name: ROUTES.INSPECTIONS,
    component: () => import(/* webpackChunkName: "MainPages" */ '@/modules/Inspection/pages/list/FlInspections.vue'),
    meta: {
      title: 'Приемки',
      layout: CABINET_LAYOUT
    }
  },
  {
    path: '/inspections/:id',
    props: (route) => ({id: Number(route.params.id)}),
    name: ROUTES.INSPECTION,
    component: () => import('@/modules/Inspection/pages/card/FlInspectionPage.vue'),
    meta: {
      title: (to) => `Приемка #${to.params.id}`,
      layout: CABINET_LAYOUT
    }
  },
  {
    path: '/tasks',
    name: ROUTES.TASKS,
    component: () => import('@/modules/Task/pages/FlTasksPage.vue'),
    meta: {
      title: 'Задачи',
      layout: CABINET_LAYOUT
    }
  },
  {
    path: '/companies',
    name: ROUTES.COMPANIES,
    component: () => import('@/modules/Company/pages/list/FlCompaniesPage.vue'),
    meta: {
      title: 'Задачи',
      layout: CABINET_LAYOUT
    }
  },
  {
    path: '/companies/create',
    name: ROUTES.COMPANY_CREATE,
    component: () => import('@/modules/Company/pages/card/FlCompanyEditPage.vue'),
    meta: {
      title: 'Создание компании',
      layout: CABINET_LAYOUT
    }
  },
  {
    path: '/companies/:id',
    name: ROUTES.COMPANY_EDIT,
    props: (route) => ({id: Number(route.params.id)}),
    component: () => import('@/modules/Company/pages/card/FlCompanyEditPage.vue'),
    meta: {
      title: (to) => `Компания #${to.params.id}`,
      layout: CABINET_LAYOUT
    }
  },
  {
    path: '/settings',
    name: ROUTES.SETTINGS,
    component: () => import('@/modules/Setting/pages/FlSettingsPage.vue'),
    redirect: '/settings/checkLists',
    meta: {
      title: 'Настройки',
      layout: CABINET_LAYOUT
    },
    children: [
      {
        path: 'checkLists',
        props: (route) => ({id: Number(route.params.id)}),
        name: ROUTES.SETTINGS_CHECK_LISTS,
        component: () => import('@/modules/Setting/pages/checkLists/FlCheckListTab.vue'),
        meta: {
          title: 'Настройки чек-листов',
          layout: CABINET_LAYOUT
        },
      },
      {
        path: 'documents',
        name: ROUTES.SETTINGS_DOCUMENTS,
        component: () => import('@/modules/Setting/pages/documents/FlDocumentsTab.vue'),
        meta: {
          title: 'Документы',
          layout: CABINET_LAYOUT
        },
      },
      {
        path: 'templates',
        name: ROUTES.SETTINGS_PRINT_TEMPLATES,
        component: () => import('@/modules/Setting/pages/printTemplates/FlPrintTemplatesTab.vue'),
        meta: {
          title: 'Печатные шаблоны',
          layout: CABINET_LAYOUT
        }
      },
      {
        path: 'profile',
        name: ROUTES.SETTINGS_PROFILE,
        component: () => import('@/modules/Setting/pages/profile/FlProfileTab.vue'),
        meta: {
          title: 'Профиль',
          layout: CABINET_LAYOUT
        }
      },
    ]
  },
  {
    path: '/404',
    name: ROUTES.NOT_FOUND,
    component: () => import('@/modules/NotFound/pages/notFound/FlNotFoundPage.vue'),
    meta: {
      title: '404',
      layout: EMPTY_LAYOUT,
      requiresAuth: false,
    }
  },
]


const NAVIGATION_DUPLICATED_ERROR_NAME = 'NavigationDuplicated'
const originalRouterPush = VueRouter.prototype.push;
const originalRouterReplace = VueRouter.prototype.replace

const hideNavigationDuplicated = result => {
  if(!result) return result
  return result.catch(err => {
    if(err.name === NAVIGATION_DUPLICATED_ERROR_NAME) return
    throw err
  })
}

VueRouter.prototype.push = function (...args) {
  const res = originalRouterPush.apply(this, args);
  return hideNavigationDuplicated(res)
}

VueRouter.prototype.replace = function (...args) {
  const res = originalRouterReplace.apply(this, args);
  return hideNavigationDuplicated(res)
}

const router = new VueRouter({
  mode: 'history',
  base: '/cabinet/',
  routes
})

router.beforeEach(async (to, from, next) => {
  await Vue.nextTick()
  const layoutStore = useLayoutStore()

  const requiresAuth = to.meta.requiresAuth ?? true
  const userStore = useUserStore()

  if(!userStore.isAuthorization) {
    layoutStore.showLoading(AUTH_LOADING_KEY, true)
    await userStore.initToken()
  } else {
    layoutStore.startProgress()
  }

  if(requiresAuth) {
    if(!userStore.isAuthorization) {
      userStore.setUnauthorizedTargetRoute(to)
      next({
        name: ROUTES.LOGIN,
        replace: true
      })
      return
    } else {
      if(userStore.isClient) {
        goToClientCabinet()
        return
      }
    }
  }

  if(userStore.isAuthorization && [ROUTES.LOGIN, ROUTES.REGISTRATION].includes(to.name)) {
    next({name: CABINET_MAIN_PAGE, replace: true})
    return
  }

  const rawTitle = to.meta.title
  const title = typeof rawTitle === 'function' ? rawTitle(to) : rawTitle
  document.title = title ? `${title} | ${DEFAULT_TITLE}` : DEFAULT_TITLE
  next()
})

router.afterEach(() => {
  const layoutStore = useLayoutStore()
  layoutStore.doneProgress()
  if (layoutStore.needHideLayout) layoutStore.hideLoading(AUTH_LOADING_KEY)
})

export {router}
